/**
 * 2N BLE Service
 * 
 * This service handles Bluetooth Low Energy (BLE) communication with 2N intercom devices.
 * It implements the Mobile Key functionality for door unlocking using Bluetooth.
 * 
 * KEY EXCHANGE MECHANISM:
 * The 2N system uses RSA key pairs for secure authentication:
 * - The intercom stores up to 4 private keys (only 1 is current, others are for verification)
 * - Mobile devices store the corresponding public keys
 * - When keys become outdated, the intercom challenges the device to exchange them
 * - This service automatically handles key refresh when 'invalidKeyId' errors occur
 * 
 * AUTOMATIC KEY REFRESH:
 * - Listens for 'authFailure' events with reason 'invalidKeyId'
 * - Automatically fetches fresh credentials from DynamoDB
 * - Re-imports credentials to the native BLE framework
 * - Retries the unlock operation with fresh keys
 * - Runs periodic refresh every 24 hours to prevent issues
 * 
 * USAGE:
 * 1. Call initializeBLE() to set up the service
 * 2. Call importAndActivateDeviceCredentials() to load credentials
 * 3. Use unlockDoor() for manual unlocking
 * 4. The service handles automatic key exchange in the background
 */

import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { PluginListenerHandle } from '@capacitor/core';
import { DynamoDBService, BluetoothCredentialSet } from './dynamodb.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Device } from '@capacitor/device';

// Import the TwoNPlugin and types from the 2n-intercom plugin
import { 
  TwoNPlugin, 
  BleDevice, 
  BleAuthEvent, 
  BleDeviceEvent,
  BleDeviceScannedEvent 
} from '@Vessel-Tech/2n-intercom';

@Injectable({
  providedIn: 'root'
})
export class TwoNBleService {
  private readonly LOG_PREFIX = '[TwoNBleService]';

  private _isBleInitialized = new BehaviorSubject<boolean>(false);
  public isBleInitialized$: Observable<boolean> = this._isBleInitialized.asObservable();

  private _bleError = new BehaviorSubject<string | null>(null);
  public bleError$: Observable<string | null> = this._bleError.asObservable();

  private _scannedDevices = new BehaviorSubject<BleDevice[]>([]);
  public scannedDevices$: Observable<BleDevice[]> = this._scannedDevices.asObservable();

  private _activeDevice = new BehaviorSubject<BleDevice | null>(null);
  public activeDevice$: Observable<BleDevice | null> = this._activeDevice.asObservable();

  /**
   * Synchronous getter for checking if BLE is initialized.
   * Use this instead of accessing private _isBleInitialized directly.
   */
  public get isBleReady(): boolean {
    return this._isBleInitialized.value;
  }

  /**
   * Synchronous getter for checking if there's an active device.
   * Use this instead of accessing private _activeDevice directly.
   */
  public get hasActiveDevice(): boolean {
    return this._activeDevice.value !== null;
  }

  /**
   * Synchronous getter for the current active device.
   * Returns null if no device is active.
   */
  public get currentActiveDevice(): BleDevice | null {
    return this._activeDevice.value;
  }

  private listenerHandles: PluginListenerHandle[] = [];

  // Platform compatibility - checked once at startup
  private isPlatformCompatible = false;
  private platformCheckCompleted = false;

  // Track pending unlock retries to prevent race conditions
  private pendingUnlockRetries = new Map<string, { timerId: ReturnType<typeof setTimeout>; attemptCount: number }>();

  // Constants for retry logic and credential refresh
  private readonly MAX_UNLOCK_RETRY_ATTEMPTS = 3;
  private readonly INITIAL_RETRY_DELAY_MS = 2000;
  private readonly CREDENTIAL_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

  // Timer for periodic credential refresh — stored so multiple calls to
  // startPeriodicCredentialRefresh() don't accumulate concurrent timers
  // (would happen on logout→login cycles).
  private credentialRefreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private platform: Platform,
    private dynamoDBService: DynamoDBService
  ) {
    this.platform.ready().then(() => {
      this.checkPlatformCompatibilityOnce().then(() => {
        if (this.isPlatformCompatible) {
          // this.log('Platform is compatible, setting up BLE event listeners');
          this.addEventListeners();
        } else {
          // this.log('Platform not compatible (web/simulator), BLE service disabled');
        }
      }).catch(error => {
        this.error('Error checking platform compatibility:', error);
        // this.log('Assuming incompatible platform, BLE service disabled');
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
      });
    });
  }

  private log(message: string, ...args: any[]) {
    console.log(`${this.LOG_PREFIX} ${message}`, ...args);
  }

  private error(message: string, errorDetails?: any, ...args: any[]) {
    const errorMessage = errorDetails instanceof Error ? errorDetails.message : String(errorDetails !== undefined && errorDetails !== null ? errorDetails : '');
    console.error(`${this.LOG_PREFIX} ERROR: ${message}`, errorMessage, ...args);
    this._bleError.next(`${message}${errorMessage ? ': ' + errorMessage : ''}`);
  }

  /**
   * Check platform compatibility once at startup and cache the result
   */
  private async checkPlatformCompatibilityOnce(): Promise<void> {
    if (this.platformCheckCompleted) {
      return;
    }

    try {
      // First check: Must be running on Capacitor (not web)
      if (!this.platform.is('capacitor')) {
        // this.log('Running on web platform, BLE services not available');
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
        return;
      }

      // Second check: Must be iOS or Android
      if (!this.platform.is('ios') && !this.platform.is('android')) {
        // this.log('Platform is not iOS or Android, BLE services not available');
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
        return;
      }

      // Third check: Must not be running on simulator/emulator
      try {
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.isVirtual) {
          // this.log('Running on simulator/emulator, BLE services not available');
          this.log('Device info:', {
            platform: deviceInfo.platform,
            model: deviceInfo.model,
            isVirtual: deviceInfo.isVirtual
          });
          this.isPlatformCompatible = false;
        } else {
          this.log('Running on real device, BLE services available');
          this.log('Device info:', {
            platform: deviceInfo.platform,
            model: deviceInfo.model,
            isVirtual: deviceInfo.isVirtual
          });
          this.isPlatformCompatible = true;
        }
      } catch (deviceError) {
        this.error('Error getting device info:', deviceError);
        // If we can't get device info, assume it's not compatible for safety
        this.isPlatformCompatible = false;
      }
    } catch (error) {
      this.error('Error in platform compatibility check:', error);
      this.isPlatformCompatible = false;
    }

    this.platformCheckCompleted = true;
  }

  /**
   * Check if BLE services are available on the current platform
   * Returns the cached result from startup check
   */
  public isPlatformSupported(): boolean {
    return this.isPlatformCompatible;
  }

  private async addEventListeners() {
    if (!this.isPlatformCompatible) {
      return;
    }

    this.listenerHandles.push(
      await TwoNPlugin.addListener('bleDeviceScanned', (device: BleDeviceScannedEvent) => {
        // this.log('bleDeviceScanned event:', device);
        // Convert scanned event to BleDevice format
        const bleDevice: BleDevice = {
          uuid: device.uuid,
          name: device.name,
          location: device.location,
          isActivated: device.activated,
          minRssi: 0, // Will be updated from actual device data
          maxRssi: 0,
          useDynamicThreshold: false,
          isCardModeActivated: false,
          isTouchModeActivated: false,
          isReliable: false
        };
        
        const currentDevices = this._scannedDevices.value;
        const existingIndex = currentDevices.findIndex(d => d.uuid === device.uuid);
        if (existingIndex > -1) {
          currentDevices[existingIndex] = { ...currentDevices[existingIndex], ...bleDevice };
        } else {
          currentDevices.push(bleDevice);
        }
        this._scannedDevices.next([...currentDevices]);
      })
    );

    this.listenerHandles.push(
      await TwoNPlugin.addListener('bleAuthEvent', (event: BleAuthEvent) => {
        this.log('bleAuthEvent event:', event);
        if (event.event === 'doorOpened') {
          this.log('Door opened successfully via BLE for device:', event.uuid);
        }
        if (event.event === 'authSuccess') {
          this.log('BLE Authentication successful for device:', event.uuid);
        }
        if (event.event === 'authFailure') {
          this.error(`BLE Authentication failed for device: ${event.uuid}`, event.reason);

          // Handle invalidKeyId specifically - triggers credential refresh
          if (event.reason === 'invalidKeyId') {
            this.log('InvalidKeyId detected, triggering credential refresh...');
            this.handleInvalidKeyId(event.uuid).catch(error => {
              this.error('Error handling invalidKeyId:', error);
            });
          }
        }
        if (event.event === 'authContinue') {
          this.log(`BLE Authentication continuing for device: ${event.uuid}, credential needed: ${event.credential}`);
          // For now, just log these. In the future, you might want to handle PIN/card/finger prompts
        }
      })
    );

    this.listenerHandles.push(
      await TwoNPlugin.addListener('bleDevicePaired', (event: BleDeviceEvent) => {
        this.log('bleDevicePaired event:', event);
      })
    );

    this.listenerHandles.push(
      await TwoNPlugin.addListener('bleDeviceDisconnected', (event: BleDeviceEvent) => {
        this.log('bleDeviceDisconnected event:', event);

        // Handle invalidKeyId disconnections - triggers credential refresh
        if (event.reason === 'invalidKeyId') {
          this.log('Device disconnected due to invalidKeyId, triggering credential refresh...');
          this.handleInvalidKeyId(event.uuid).catch(error => {
            this.error('Error handling invalidKeyId on disconnect:', error);
          });
        }
      })
    );

    // Handle credential refresh needed events
    this.listenerHandles.push(
      await TwoNPlugin.addListener('bleCredentialRefreshNeeded', (event: any) => {
        this.log('bleCredentialRefreshNeeded event:', event);
        this.handleCredentialRefreshNeeded(event).catch(error => {
          this.error('Error handling credential refresh needed:', error);
        });
      })
    );

    this.log('BLE event listeners registered successfully');
  }

  async initializeBLE(): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, skipping BLE initialization');
      this._isBleInitialized.next(false);
      return false;
    }

    try {
      this.log('Initializing BLE...');
      this._bleError.next(null);

      this.log('Requesting location permission...');
      const permResult = await TwoNPlugin.requestLocationPermission();
      this.log('Location permission status:', permResult?.status);

      this.log('Checking Bluetooth status...');
      const bleStatus = await TwoNPlugin.checkBluetoothStatus();
      this.log('Bluetooth status:', bleStatus);
      if (!bleStatus?.available) {
        this.error('Bluetooth is not available or not powered on.', bleStatus?.status);
        this._isBleInitialized.next(false);
        return false;
      }

      this.log('Setting up Bluetooth manager in native plugin...');
      const setupResult = await TwoNPlugin.setupBluetoothManager({
        cardModeEnabled: true,
        cardModeThreshold: -65,
        useSensors: true,
        securityLevel: 'default' as 'default',
        debugNotifications: false,
        debugVibration: false,
      });
      this.log('Bluetooth manager setup result:', setupResult);
      if (!setupResult?.success) {
        this.error('Failed to setup Bluetooth manager.', setupResult?.message);
        this._isBleInitialized.next(false);
        return false;
      }
      
      this._isBleInitialized.next(true);
      this.log('BLE initialized successfully.');
      return true;

    } catch (err) {
      this.error('Error during BLE initialization:', err);
      this._isBleInitialized.next(false);
      return false;
    }
  }

  async importAndActivateDeviceCredentials(): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, skipping credential import');
      return false;
    }

    if (!this._isBleInitialized.value) {
      this.error('BLE not initialized. Cannot import credentials.');
      return false;
    }
    this.log('Attempting to import and activate BLE device credentials...');
    this._bleError.next(null);

    try {
      // Add retry logic for DynamoDB access since it might take time for auth to complete
      let bluetoothCredentials: BluetoothCredentialSet | null = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries && !bluetoothCredentials) {
        try {
          const credentialsResult = await this.dynamoDBService.getResidentWithAllCredentials();
          bluetoothCredentials = credentialsResult.bluetoothCredentials || null;
          
          if (!bluetoothCredentials && retryCount < maxRetries - 1) {
            this.log(`No Bluetooth credentials found, retry ${retryCount + 1}/${maxRetries} in 2 seconds...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (error) {
          this.log(`Error getting credentials, retry ${retryCount + 1}/${maxRetries}:`, error);
          if (retryCount < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        retryCount++;
      }

      if (!bluetoothCredentials) {
        this.error('No Bluetooth credentials found in DynamoDB for the current user after retries.');
        return false;
      }
      
      this.log('Found Bluetooth credentials:', bluetoothCredentials);

      const pluginCredentials = [{
        location: bluetoothCredentials.locationId,
        keyId: bluetoothCredentials.keyId,
        pubKey: bluetoothCredentials.publicKey,
        authId: bluetoothCredentials.authId
      }];

      this.log('Importing credentials to native plugin...', pluginCredentials);
      const saveResult = await TwoNPlugin.importCredentials({ locations: pluginCredentials as any });

      this.log('Credentials import result:', saveResult);

      if (!saveResult?.success) {
        this.error('Failed to import BLE credentials.', saveResult?.message);
        return false;
      }

      this.log('BLE credentials stored for locations:', saveResult.count);
      
      this.log('Retrieving BLE devices from plugin to find the imported one...');
      const devicesResult = await TwoNPlugin.getBleDevices();
      const allPluginDevices: BleDevice[] = devicesResult?.devices || [];
      this.log('Available BLE devices from plugin:', allPluginDevices);

      const importedDevice = allPluginDevices.find(device => {
        // The actual data structure from iOS has nested objects
        const deviceData = device as any;
        return deviceData.info?.location === bluetoothCredentials!.locationId || 
               deviceData.location?.location === bluetoothCredentials!.locationId ||
               device.location === bluetoothCredentials!.locationId; // fallback for flat structure
      });

      if (!importedDevice) {
        // No device discovered yet — this is normal on Android after fresh credential import.
        // The BLE scanner will auto-pair when it discovers a matching device nearby.
        this.log(`No device found yet for locationId: ${bluetoothCredentials!.locationId} — waiting for BLE auto-discovery`);
        return true;
      }
      
      this.log('Found imported device:', importedDevice);

      if (importedDevice.isActivated) {
        this.log('Device already activated:', importedDevice.uuid);
        this._activeDevice.next(importedDevice);
        return true;
      }

      this.log('Activating device:', importedDevice.uuid);
      const activateResult = await TwoNPlugin.setDeviceActive({
        uuid: importedDevice.uuid,
        active: true
      });
      this.log('Device activation result:', activateResult);

      if (activateResult?.success) {
        this.log('Device activated successfully:', importedDevice.uuid);
        this._activeDevice.next({ ...importedDevice, isActivated: true });
        return true;
      } else {
        this.error('Failed to activate device: ' + importedDevice.uuid, activateResult?.message);
        return false;
      }

    } catch (err) {
      this.error('Error during credential import and activation:', err);
      return false;
    }
  }

  async unlockDoor(deviceUuid?: string): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, cannot unlock door via BLE');
      return false;
    }

    const targetUuid = deviceUuid || this._activeDevice.value?.uuid;

    if (!targetUuid) {
      this.error('No active device UUID available to unlock door.');
      return false;
    }
    if (!this._isBleInitialized.value) {
      this.error('BLE not initialized. Cannot unlock door.');
      return false;
    }
    this.log(`Attempting to unlock door for device UUID: ${targetUuid}`);
    this._bleError.next(null);

    try {
      const unlockResult = await TwoNPlugin.unlockDoorWithBle({ uuid: targetUuid });
      this.log('Unlock door result:', unlockResult);
      if (unlockResult?.success) {
        this.log('Unlock signal sent successfully for UUID:', targetUuid);
        return true;
      } else {
        this.error('Failed to send unlock signal for UUID: ' + targetUuid, unlockResult?.message);
        return false;
      }
    } catch (err) {
      this.error('Error unlocking door for UUID: ' + targetUuid, err);
      return false;
    }
  }

  async refreshBleDevicesFromPlugin(): Promise<BleDevice[]> {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, returning empty device list');
      return [];
    }
    try {
      this.log('Refreshing BLE devices from plugin...');
      const devicesResult = await TwoNPlugin.getBleDevices();
      const pluginDevices: BleDevice[] = devicesResult?.devices || [];
      this._scannedDevices.next(pluginDevices);
      
      const currentActive = this._activeDevice.value;
      if (currentActive) {
        const updatedActiveDevice = pluginDevices.find(d => d.uuid === currentActive.uuid);
        if (updatedActiveDevice) {
          this._activeDevice.next(updatedActiveDevice);
        } else {
          this._activeDevice.next(null);
        }
      }
      return pluginDevices;
    } catch (err) {
      this.error('Error refreshing BLE devices from plugin:', err);
      return [];
    }
  }

  async pairDeviceWithPin(uuid: string, pin: string): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, cannot pair device');
      return false;
    }
    this.log(`Attempting to pair device ${uuid} with PIN...`);
    try {
      const pairResult = await TwoNPlugin.pairDevice({ uuid, pin });
      this.log('Pairing initiation result:', pairResult);
      return pairResult?.success || false;
    } catch (err) {
      this.error(`Error initiating pairing for ${uuid}:`, err);
      return false;
    }
  }

  async unlockDevice(uuid: string): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, cannot unlock device');
      return false;
    }
    this.log(`Attempting to unlock device ${uuid}...`);
    try {
      const unlockResult = await TwoNPlugin.unlockDoorWithBle({ uuid });
      this.log('Unlock initiation result:', unlockResult);
      return unlockResult?.success || false;
    } catch (err) {
      this.error(`Error initiating unlock for ${uuid}:`, err);
      return false;
    }
  }

  private async handleInvalidKeyId(uuid: string): Promise<void> {
    this.log(`Handling invalidKeyId for device: ${uuid}`);

    // Check if there's already a pending retry for this device
    const existingRetry = this.pendingUnlockRetries.get(uuid);
    if (existingRetry) {
      this.log(`Pending unlock retry already exists for device ${uuid}, skipping duplicate`);
      return;
    }

    try {
      // Trigger automatic credential refresh
      this.log('Attempting to refresh BLE credentials due to invalidKeyId...');

      // Re-import credentials from DynamoDB to get fresh keys
      const refreshSuccess = await this.importAndActivateDeviceCredentials();

      if (refreshSuccess) {
        this.log('Successfully refreshed credentials after invalidKeyId');

        // Schedule retry with tracking to prevent race conditions
        this.scheduleUnlockRetry(uuid, 1);
      } else {
        this.error('Failed to refresh credentials after invalidKeyId');
      }
    } catch (error) {
      this.error('Error in handleInvalidKeyId:', error);
    }
  }

  /**
   * Schedules an unlock retry with exponential backoff and proper cleanup.
   * Prevents duplicate retries and limits the number of attempts.
   */
  private scheduleUnlockRetry(uuid: string, attemptNumber: number): void {
    // Check if max attempts exceeded
    if (attemptNumber > this.MAX_UNLOCK_RETRY_ATTEMPTS) {
      this.log(`Max unlock retry attempts (${this.MAX_UNLOCK_RETRY_ATTEMPTS}) exceeded for device ${uuid}`);
      this.pendingUnlockRetries.delete(uuid);
      return;
    }

    // Cancel any existing retry for this device
    this.cancelPendingUnlockRetry(uuid);

    // Calculate delay with exponential backoff
    const delay = this.INITIAL_RETRY_DELAY_MS * Math.pow(2, attemptNumber - 1);
    this.log(`Scheduling unlock retry #${attemptNumber} for device ${uuid} in ${delay}ms`);

    const timerId = setTimeout(async () => {
      this.log(`Executing unlock retry #${attemptNumber} for device ${uuid}...`);
      this.pendingUnlockRetries.delete(uuid);

      try {
        const success = await this.unlockDevice(uuid);
        if (!success && attemptNumber < this.MAX_UNLOCK_RETRY_ATTEMPTS) {
          this.log(`Unlock retry #${attemptNumber} failed, scheduling next attempt`);
          this.scheduleUnlockRetry(uuid, attemptNumber + 1);
        } else if (success) {
          this.log(`Unlock retry #${attemptNumber} succeeded`);
        }
      } catch (error) {
        this.error(`Unlock retry #${attemptNumber} threw error:`, error);
        if (attemptNumber < this.MAX_UNLOCK_RETRY_ATTEMPTS) {
          this.scheduleUnlockRetry(uuid, attemptNumber + 1);
        }
      }
    }, delay);

    this.pendingUnlockRetries.set(uuid, { timerId, attemptCount: attemptNumber });
  }

  /**
   * Cancels a pending unlock retry for a specific device.
   */
  private cancelPendingUnlockRetry(uuid: string): void {
    const pending = this.pendingUnlockRetries.get(uuid);
    if (pending) {
      clearTimeout(pending.timerId);
      this.pendingUnlockRetries.delete(uuid);
      this.log(`Cancelled pending unlock retry for device ${uuid}`);
    }
  }

  /**
   * Cancels all pending unlock retries (e.g., on service destruction).
   */
  private cancelAllPendingUnlockRetries(): void {
    for (const [uuid, pending] of this.pendingUnlockRetries) {
      clearTimeout(pending.timerId);
      this.log(`Cancelled pending unlock retry for device ${uuid}`);
    }
    this.pendingUnlockRetries.clear();
  }

  private async handleCredentialRefreshNeeded(event: any): Promise<void> {
    this.log(`Handling credential refresh needed for device: ${event.uuid}, location: ${event.location}, reason: ${event.reason}`);
    
    try {
      // Trigger automatic credential refresh
      this.log('Attempting to refresh BLE credentials...');
      
      // Re-import credentials from DynamoDB to get fresh keys
      const refreshSuccess = await this.importAndActivateDeviceCredentials();
      
      if (refreshSuccess) {
        this.log('Successfully refreshed credentials');
        
        // Notify that credentials have been refreshed
        this.log(`Credentials refreshed for location: ${event.location}`);
      } else {
        this.error('Failed to refresh credentials');
      }
    } catch (error) {
      this.error('Error in handleCredentialRefreshNeeded:', error);
    }
  }

  /**
   * Periodically refresh BLE credentials to prevent invalidKeyId errors
   * This should be called regularly (e.g., every 24 hours) to keep keys fresh
   */
  async refreshCredentialsPeriodically(): Promise<boolean> {
    if (!this._isBleInitialized.value) {
      this.log('BLE not initialized, skipping periodic credential refresh');
      return false;
    }

    this.log('Starting periodic BLE credential refresh...');
    
    try {
      // Get fresh credentials from DynamoDB and re-import them
      const refreshSuccess = await this.importAndActivateDeviceCredentials();
      
      if (refreshSuccess) {
        this.log('Periodic credential refresh completed successfully');
        return true;
      } else {
        this.error('Periodic credential refresh failed');
        return false;
      }
    } catch (error) {
      this.error('Error during periodic credential refresh:', error);
      return false;
    }
  }

  /**
   * Start automatic periodic credential refresh
   * Call this after successful BLE initialization
   */
  startPeriodicCredentialRefresh(): void {
    if (this.credentialRefreshTimer) {
      this.log('Periodic credential refresh already running; skipping duplicate start');
      return;
    }

    this.log(`Starting periodic credential refresh (every ${this.CREDENTIAL_REFRESH_INTERVAL_MS / (60 * 60 * 1000)} hours)`);

    this.credentialRefreshTimer = setInterval(async () => {
      this.log('Executing scheduled credential refresh...');
      await this.refreshCredentialsPeriodically();
    }, this.CREDENTIAL_REFRESH_INTERVAL_MS);
  }

  /** Stop the periodic credential refresh timer if running. */
  stopPeriodicCredentialRefresh(): void {
    if (this.credentialRefreshTimer) {
      clearInterval(this.credentialRefreshTimer);
      this.credentialRefreshTimer = null;
      this.log('Stopped periodic credential refresh');
    }
  }

  /**
   * Disables BLE functionality and cleans up resources
   * Call this when the user turns off the phone as key feature
   */
  async disableBLE(): Promise<boolean> {
    if (!this._isBleInitialized.value) {
      this.log('BLE not initialized, nothing to disable');
      return true;
    }

    try {
      this.log('Disabling BLE functionality...');

      // Stop the periodic credential refresh timer
      this.stopPeriodicCredentialRefresh();

      // Clean up all event listeners
      this.cleanupListeners();

      // Clear all BLE data from the native plugin
      const clearResult = await TwoNPlugin.clearAllBleData();
      
      if (clearResult?.success) {
        this.log('Successfully cleared all BLE data');
      } else {
        this.error('Failed to clear BLE data:', clearResult?.message);
      }
      
      // Reset state
      this._isBleInitialized.next(false);
      this._scannedDevices.next([]);
      this._activeDevice.next(null);
      this._bleError.next(null);
      
      this.log('BLE functionality disabled successfully');
      return true;
    } catch (error) {
      this.error('Error disabling BLE:', error);
      return false;
    }
  }

  // Cleanup listeners when the service is destroyed (e.g., on component ngOnDestroy)
  cleanupListeners(): void {
    if (!this.isPlatformCompatible) {
      this.log('Platform not compatible, no native listeners to clean up');
      return;
    }

    this.log('Cleaning up BLE event listeners...');
    this.listenerHandles.forEach(handle => handle.remove());
    this.listenerHandles = [];

    // Cancel any pending unlock retries to prevent memory leaks
    this.cancelAllPendingUnlockRetries();
  }
} 